import {
    Grid,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Box,
    Divider,
    CardActions,
    CircularProgress,
} from "@mui/material";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

import { FormInputText } from "../../../components/form/FormInputText";
import { RoundButton } from "../../../_styles/StyledButtons";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { forgotPasswordFormSchema } from "../../../_validators/schemas/profileForm.schema";
import { useTranslation } from "react-i18next";

function ForgotPasswordForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { profileStore, authStore } = useStore();
    const { sendPasswordResetEmail, loading } = profileStore;
    const { user } = authStore;
    const { handleSubmit, reset, control } = useForm<any>({
        resolver: yupResolver(forgotPasswordFormSchema()),
        defaultValues: {
            email: user?.email ?? "",
        },
    });

    const onSubmit = async (data: any) => {
        await sendPasswordResetEmail(data.email);
        toast.info(t("TOAST_USER_RESET"));
        reset();
    };

    const goBack = () => {
        navigate(-1);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flex: 1,
                height: "100%",
                backgroundColor: "#ffffff",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            {!user && (
                <Box sx={{ display: "flex", alignSelf: "center" }}>
                    <Typography
                        component="h1"
                        variant="h3"
                        color="#03b2ff"
                        fontWeight={500}
                        paddingBottom={3}
                    >
                        SPIRIT
                    </Typography>
                </Box>
            )}
            <Grid
                container
                justifyContent={"center"}
                sx={{ alignSelf: "center", backgroundColor: "#F1F4F9", padding: 5 }}
            >
                <Card
                    sx={{ maxWidth: 450, backgroundColor: "#ffffff" }}
                    variant="outlined"
                >
                    <CardHeader sx={{ display: "flex" }} title={t("PW_FIND_ACCOUNT")} />
                    <Divider />
                    <CardContent sx={{ paddingBottom: "5px !important" }}>
                        <Typography component="p">
                            {t("PW_RESET_MSG")}
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={handleSubmit(onSubmit)}
                            sx={{ mt: 2 }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormInputText
                                        name="email"
                                        control={control}
                                        label={t("PW_RESET_EMAIL")}
                                    />
                                </Grid>
                            </Grid>
                            <Divider />
                            <CardActions
                                disableSpacing
                                sx={{ justifyContent: "right", padding: 0, alignItems: "center" }}
                            >
                                <RoundButton
                                    color="inherit"
                                    variant="text"
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        mr: 1,
                                        "&:hover": { backgroundColor: "transparent" },
                                    }}
                                    disableTouchRipple
                                    onClick={goBack}
                                    title="Cancel"
                                    data-testid="cancel-button"
                                >
                                    <Typography fontSize="13px">{t("GENERAL_CANCEL")}</Typography>
                                </RoundButton>
                                <RoundButton
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={loading}
                                    title="Search"
                                    data-testid="submit-button"
                                >
                                    {(loading && <CircularProgress size={25} />) || (
                                        <Typography fontSize="13px">{t("PW_RESET_BTN")}</Typography>
                                    )}
                                </RoundButton>
                            </CardActions>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Box>
    );
}

export default observer(ForgotPasswordForm);
